import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';

// Shared chrome for the billing page's KPI recap row. Styled to the
// dashboard design system (CLAUDE.md): rounded-xl, shadow-sm, border-gray-100
// — not files-viewer's border-gray-200/shadow-[…] card, which belongs to the
// admin app. (A generic `BillingCard` chart-container wrapper used to live
// here too, for the Overview tab's charts — removed along with those charts;
// see git history if a content-card wrapper is needed again.)

export function InfoHint({ text }: { text: string }) {
    return (
        <Tooltip content={text} focusable>
            <span className="text-gray-300 hover:text-gray-500 transition-colors cursor-help inline-flex shrink-0">
                <InformationCircleIcon className="h-3.5 w-3.5" />
            </span>
        </Tooltip>
    );
}

export type KpiTone = 'default' | 'alert';

interface KpiStatProps {
    label: string;
    info?: string;
    value: string;
    sub?: string | null;
    tone?: KpiTone;
    action?: React.ReactNode;
}

// `tone="alert"` only reddens the value (and sub-label) text — the card
// itself always uses the same neutral chrome as every other KPI card, so an
// overdue balance doesn't turn the whole tile into a red alert box.
export function KpiStat({ label, info, value, sub, tone = 'default', action }: KpiStatProps) {
    return (
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm p-4 flex flex-col gap-1.5 min-w-0">
            <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                {label}
                {info && <InfoHint text={info} />}
            </span>
            <span
                className={`text-2xl font-bold tabular-nums ${tone === 'alert' ? 'text-red-600' : 'text-gray-800'}`}
            >
                {value}
            </span>
            {sub && (
                <span className={`text-xs ${tone === 'alert' ? 'text-red-500' : 'text-gray-400'}`}>
                    {sub}
                </span>
            )}
            {action}
        </div>
    );
}

// Placeholder shown in the KPI row while the summary query is in flight —
// keeps the row's height stable so the tab bar below it doesn't jump.
export function KpiStatSkeleton() {
    return (
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm p-4 flex flex-col gap-2 min-w-0 animate-pulse">
            <div className="h-3 w-20 bg-gray-200 rounded" />
            <div className="h-6 w-24 bg-gray-200 rounded" />
            <div className="h-2.5 w-28 bg-gray-100 rounded" />
        </div>
    );
}
